import { OPEN_ORDER_STATUSES, type OrderStatus } from './constants'
import { AppError } from './errors'

/**
 * FR-ORD-4 — the single definition of legal order movement.
 * The API worker, the KDS and the POS all import this; nothing hand-rolls transitions.
 */
const TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  pending_payment: ['placed', 'cancelled'],
  placed: ['accepted', 'cancelled'],
  accepted: ['preparing', 'cancelled'],
  preparing: ['ready', 'cancelled'],
  ready: ['delivered', 'cancelled'],
  // Food that reached the customer is refunded, never "cancelled".
  delivered: ['refunded'],
  cancelled: ['refunded'],
  refunded: [],
}

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return TRANSITIONS[from].includes(to)
}

export function allowedTransitions(from: OrderStatus): readonly OrderStatus[] {
  return TRANSITIONS[from]
}

export function assertTransition(from: OrderStatus, to: OrderStatus): void {
  if (!canTransition(from, to)) {
    throw new AppError(
      'CONFLICT_STATUS_TRANSITION',
      `CONFLICT_STATUS_TRANSITION: cannot move order from "${from}" to "${to}"`,
      { from, to, allowed: TRANSITIONS[from] },
    )
  }
}

export function isTerminal(status: OrderStatus): boolean {
  return TRANSITIONS[status].length === 0
}

/** Orders the kitchen is responsible for right now. */
export function isOpen(status: OrderStatus): boolean {
  return (OPEN_ORDER_STATUSES as readonly OrderStatus[]).includes(status)
}

/** FR-KDS-1 — one tap advances an order along the happy path. */
const KITCHEN_PATH: Partial<Record<OrderStatus, OrderStatus>> = {
  placed: 'accepted',
  accepted: 'preparing',
  preparing: 'ready',
  ready: 'delivered',
}

export function nextKitchenStatus(status: OrderStatus): OrderStatus | null {
  return KITCHEN_PATH[status] ?? null
}

export const KDS_COLUMNS = ['new', 'preparing', 'ready', 'delivered'] as const
export type KdsColumn = (typeof KDS_COLUMNS)[number]

/** FR-KDS-1 — which of the four columns an order belongs in, if any. */
const COLUMN_BY_STATUS: Partial<Record<OrderStatus, KdsColumn>> = {
  placed: 'new',
  accepted: 'preparing',
  preparing: 'preparing',
  ready: 'ready',
  delivered: 'delivered',
}

export function kdsColumnFor(status: OrderStatus): KdsColumn | null {
  return COLUMN_BY_STATUS[status] ?? null
}

/** The status a column's cards move to when dropped there (FR-KDS-1 drag & drop). */
export const STATUS_FOR_COLUMN: Record<KdsColumn, OrderStatus> = {
  new: 'placed',
  preparing: 'preparing',
  ready: 'ready',
  delivered: 'delivered',
}
